// Main Garden Viewer - coordinates scene and data loading

import * as THREE from 'three'
import { api, demoApi, auth } from '@/api'
import { GardenScene, PlantRenderer, BuildingRenderer, GroundRenderer, SchemeRenderer, elevationService, modelLoader, texturePreloader, type ViewMode, type LayerName, type LayerVisibility, type SelectableObject } from '@/scene'
import { debug } from '@/utils/debug'
import { isEditMode, isMcpEnabled } from '@/utils/editMode'
import { t } from '@/i18n'
import type { Garden, Plant, Building, Product, ProductFile, Layer, GroundShape, SchemeOutline } from '@/types'
import { ZoneRenderer, PinRenderer, zoneState, exposeZoneAPI, initWebSocketBridge, disposeWebSocketBridge } from '@/zones'
import { setGeoAnchor, getGeoAnchor } from '@/geodata/geoAnchorState'
import { rotateBoundaryPoints } from '@/geodata/boundaryImport'
import { setParcel } from '@/geodata/parcelState'
import type { GeoAnchor } from '@/geodata/types'
import type { ParcelMetadata } from '@/geodata/parcelState'
import { ToolManager } from '@/editor/ToolManager'
import { HandleManager } from '@/editor/handles/HandleManager'
import { SelectTool } from '@/editor/tools/SelectTool'
import { DrawRectTool } from '@/editor/tools/DrawRectTool'
import { DrawEllipseTool } from '@/editor/tools/DrawEllipseTool'
import { DrawPolygonTool } from '@/editor/tools/DrawPolygonTool'
import { DrawPathTool } from '@/editor/tools/DrawPathTool'
import { PlacePinTool } from '@/editor/tools/PlacePinTool'
import { PlaceObjectTool } from '@/editor/tools/PlaceObjectTool'
import { LinearPlacementTool } from '@/editor/tools/LinearPlacementTool'
import { RulerTool } from '@/editor/tools/RulerTool'
import { RulerManager } from '@/editor/RulerManager'
import { DimensionOverlay } from '@/ui/DimensionOverlay'
import { undoStack } from '@/editor/UndoStack'
import { getSelectedZoneType, getSelectedProduct } from '@/ui/state'
import { gardenObjectState, type StateChange } from '@/state/GardenObjectState'
import { saveService } from '@/api/SaveService'
import { editorAPI } from '@/api/EditorAPI'

export interface GardenLoadedMetrics {
  gardenId: string
  loadTimeMs: number
  objectCount: number
}

export interface ViewerOptions {
  container: HTMLElement
  demoMode?: boolean
  onStatusChange?: (status: string) => void
  onGardenLoaded?: (garden: Garden, metrics: GardenLoadedMetrics) => void
  onError?: (error: Error) => void
}

export class GardenViewer {
  private container: HTMLElement
  private scene: GardenScene | null = null
  private plantRenderer: PlantRenderer | null = null
  private buildingRenderer: BuildingRenderer | null = null
  private groundRenderer: GroundRenderer | null = null
  private schemeRenderer: SchemeRenderer | null = null
  private zoneRenderer: ZoneRenderer | null = null
  private pinRenderer: PinRenderer | null = null
  private toolManager: ToolManager | null = null
  private dimensionOverlay: DimensionOverlay | null = null
  private rulerManager: RulerManager | null = null

  private currentGarden: Garden | null = null
  private modelUrls: Map<string, ProductFile[]> = new Map()
  private textureUrls: Map<string, ProductFile[]> = new Map()  // For ground textures
  private billboardUrls: Map<string, ProductFile> = new Map()  // Billboard textures by product ID
  private products: Map<string, Product> = new Map()  // Product details (code, name, etc.)
  private categoryLayerMap: Map<string, Layer> = new Map()  // Category ID -> Layer mapping
  private isInitialized: boolean = false
  private initPromise: Promise<void> | null = null
  private demoMode: boolean = false

  private onStatusChange?: (status: string) => void
  private onGardenLoaded?: (garden: Garden, metrics: GardenLoadedMetrics) => void
  private onError?: (error: Error) => void

  constructor(options: ViewerOptions) {
    this.container = options.container
    this.demoMode = options.demoMode ?? false
    this.onStatusChange = options.onStatusChange
    this.onGardenLoaded = options.onGardenLoaded
    this.onError = options.onError
    debug.log('Viewer', `GardenViewer created${this.demoMode ? ' (demo mode)' : ''}`)
  }

  /**
   * Get the API client to use (demo or real)
   */
  private getApiClient() {
    return this.demoMode ? demoApi : api
  }

  /**
   * Check if viewer is in demo mode
   */
  isDemoMode(): boolean {
    return this.demoMode
  }

  /**
   * Initialize the 3D scene (returns promise that resolves when ready)
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      debug.log('Viewer', 'Already initializing, waiting...')
      return this.initPromise
    }

    this.initPromise = (async () => {
      try {
        this.setStatus(t('status.initializing'))
        debug.log('Scene', 'Creating GardenScene...')

        // Create scene first, then create renderers after assignment completes
        this.scene = new GardenScene({
          container: this.container
        })

        debug.success('Scene', 'GardenScene created')
        debug.log('Scene', 'Creating renderers...')

        // Now this.scene is definitely assigned
        this.plantRenderer = new PlantRenderer(this.scene)
        this.buildingRenderer = new BuildingRenderer(this.scene)
        this.groundRenderer = new GroundRenderer(this.scene)
        this.schemeRenderer = new SchemeRenderer(this.scene)

        // Initialize zone system (edit mode only)
        if (isEditMode()) {
          this.zoneRenderer = new ZoneRenderer(this.scene)
          this.pinRenderer = new PinRenderer(this.scene)
          // Wire up state changes to renderers
          zoneState.onChange((state) => {
            this.zoneRenderer?.renderZones(state.zones)
            this.pinRenderer?.renderPins(state.pins)
            // Meshes were rebuilt — refresh SelectionManager's stale reference
            const sm = this.scene?.getSelectionManager()
            const selected = sm?.getSelected()
            if (sm && selected) {
              const newZoneMesh = this.zoneRenderer?.getMesh(selected.id)
              if (newZoneMesh) {
                sm.updateSelectedMesh(selected.id, newZoneMesh)
              } else {
                const newPinMesh = this.pinRenderer?.getMesh(selected.id)
                if (newPinMesh) sm.updateSelectedMesh(selected.id, newPinMesh)
              }
            }
          })

          // Add zone/pin groups to selection targets
          const selectionManager = this.scene.getSelectionManager()
          if (selectionManager) {
            selectionManager.addSelectableGroup(this.zoneRenderer.getGroup())
            selectionManager.addSelectableGroup(this.pinRenderer.getGroup())
            debug.log('Viewer', 'Zone/Pin groups added to selection manager')

            // Initialize tool system
            const handleManager = new HandleManager(this.scene)
            this.toolManager = new ToolManager(this.scene, this.container)

            // Create dimension overlay for live measurements
            const canvas = this.scene.getRendererDomElement() as HTMLCanvasElement
            this.dimensionOverlay = new DimensionOverlay(this.container, canvas)

            const highlightManager = this.scene.getHighlightManager()
            const selectTool = new SelectTool(this.toolManager, handleManager, selectionManager, highlightManager, this.dimensionOverlay)
            this.toolManager.registerTool(selectTool)

            const drawRectTool = new DrawRectTool(this.toolManager, this.scene.getScene(), {
              overlay: this.dimensionOverlay,
              onCreate: (rect) => {
                // Dual-mode: ground shape vs metadata zone
                const product = getSelectedProduct()
                if (product) {
                  this.ensureProductTextures(product)
                  // Same as zone: x,y = center (from DrawRectTool)
                  const id = gardenObjectState.addObject('rectangle', product.id,
                    rect.x, rect.y, rect.width, rect.height, {
                    productName: product.name,
                  })
                  const snapshot = { ...gardenObjectState.getObjectById(id)! }
                  undoStack.push({
                    description: `Create ground ${id}`,
                    undo: () => gardenObjectState.deleteObject(id),
                    redo: () => gardenObjectState.restoreObject(snapshot),
                  })
                  return id
                }
                const zoneType = getSelectedZoneType()
                if (!zoneType) return ''
                const id = zoneState.addZone(zoneType, rect.x, rect.y, rect.width, rect.height)
                const snapshot = { ...zoneState.getZoneById(id)! }
                undoStack.push({
                  description: `Create ${id}`,
                  undo: () => zoneState.deleteZone(id),
                  redo: () => zoneState.restoreZone(snapshot),
                })
                return id
              }
            })
            this.toolManager.registerTool(drawRectTool)

            const drawEllipseTool = new DrawEllipseTool(this.toolManager, this.scene.getScene(), {
              overlay: this.dimensionOverlay,
              onCreate: (rect) => {
                // Dual-mode: ground shape vs metadata zone
                const product = getSelectedProduct()
                if (product) {
                  this.ensureProductTextures(product)
                  // Same as zone: x,y = center (from DrawEllipseTool)
                  const id = gardenObjectState.addObject('ellipse', product.id,
                    rect.x, rect.y, rect.width, rect.height, {
                    productName: product.name,
                  })
                  const snapshot = { ...gardenObjectState.getObjectById(id)! }
                  undoStack.push({
                    description: `Create ground ${id}`,
                    undo: () => gardenObjectState.deleteObject(id),
                    redo: () => gardenObjectState.restoreObject(snapshot),
                  })
                  return id
                }
                const zoneType = getSelectedZoneType()
                if (!zoneType) return ''
                const id = zoneState.addZone(zoneType, rect.x, rect.y, rect.width, rect.height, { shapeKind: 'ellipse' })
                const snapshot = { ...zoneState.getZoneById(id)! }
                undoStack.push({
                  description: `Create ${id}`,
                  undo: () => zoneState.deleteZone(id),
                  redo: () => zoneState.restoreZone(snapshot),
                })
                return id
              }
            })
            this.toolManager.registerTool(drawEllipseTool)

            const drawPolygonTool = new DrawPolygonTool(this.toolManager, this.scene.getScene(), {
              overlay: this.dimensionOverlay,
              onCreate: (polygon) => {
                // Dual-mode: ground shape vs metadata zone
                const product = getSelectedProduct()
                if (product) {
                  this.ensureProductTextures(product)
                  // Same as zone: x,y = centroid, points = centroid-relative
                  const id = gardenObjectState.addObject('polygon', product.id,
                    polygon.x, polygon.y, polygon.width, polygon.height, {
                    productName: product.name,
                    points: polygon.points,
                  })
                  const snapshot = { ...gardenObjectState.getObjectById(id)! }
                  undoStack.push({
                    description: `Create ground ${id}`,
                    undo: () => gardenObjectState.deleteObject(id),
                    redo: () => gardenObjectState.restoreObject(snapshot),
                  })
                  return id
                }
                const zoneType = getSelectedZoneType()
                if (!zoneType) return ''
                const id = zoneState.addZone(zoneType, polygon.x, polygon.y, polygon.width, polygon.height, {
                  points: polygon.points,
                })
                const snapshot = { ...zoneState.getZoneById(id)! }
                undoStack.push({
                  description: `Create ${id}`,
                  undo: () => zoneState.deleteZone(id),
                  redo: () => zoneState.restoreZone(snapshot),
                })
                return id
              }
            })
            this.toolManager.registerTool(drawPolygonTool)

            const drawPathTool = new DrawPathTool(this.toolManager, this.scene.getScene(), {
              overlay: this.dimensionOverlay,
              onCreate: (path) => {
                // Dual-mode: ground shape vs metadata zone
                const product = getSelectedProduct()
                if (product) {
                  this.ensureProductTextures(product)
                  const id = gardenObjectState.addObject('path', product.id,
                    path.x, path.y, path.width, path.height, {
                    productName: product.name,
                    points: path.points,
                    pathWaypoints: path.pathWaypoints,
                    pathWidth: path.pathWidth,
                  })
                  const snapshot = { ...gardenObjectState.getObjectById(id)! }
                  undoStack.push({
                    description: `Create ground ${id}`,
                    undo: () => gardenObjectState.deleteObject(id),
                    redo: () => gardenObjectState.restoreObject(snapshot),
                  })
                  return id
                }
                const zoneType = getSelectedZoneType()
                if (!zoneType) return ''
                const id = zoneState.addZone(zoneType, path.x, path.y, path.width, path.height, {
                  shapeKind: 'path',
                  points: path.points,
                  pathWaypoints: path.pathWaypoints,
                  pathWidth: path.pathWidth,
                })
                const snapshot = { ...zoneState.getZoneById(id)! }
                undoStack.push({
                  description: `Create ${id}`,
                  undo: () => zoneState.deleteZone(id),
                  redo: () => zoneState.restoreZone(snapshot),
                })
                return id
              }
            })
            this.toolManager.registerTool(drawPathTool)

            const placePinTool = new PlacePinTool(this.toolManager)
            this.toolManager.registerTool(placePinTool)

            const placeObjectTool = new PlaceObjectTool(this.toolManager)
            this.toolManager.registerTool(placeObjectTool)

            const linearPlacementTool = new LinearPlacementTool(this.toolManager, this.scene.getScene(), {
              overlay: this.dimensionOverlay,
              onCommit: (objects) => {
                const ids: string[] = []
                const snapshots: Array<ReturnType<typeof gardenObjectState.getObjectById>> = []
                for (const obj of objects) {
                  const id = gardenObjectState.addObject(
                    obj.type, obj.productId,
                    obj.x, obj.y, obj.width, obj.height,
                    {
                      rotation: obj.rotation,
                      elevation: obj.elevation,
                      productName: obj.productName,
                    }
                  )
                  ids.push(id)
                  snapshots.push({ ...gardenObjectState.getObjectById(id)! })
                }
                undoStack.push({
                  description: `Place ${ids.length} objects in line`,
                  undo: () => { for (const id of ids) gardenObjectState.deleteObject(id) },
                  redo: () => { for (const snap of snapshots) if (snap) gardenObjectState.restoreObject(snap) },
                })
                return ids
              },
            })
            this.toolManager.registerTool(linearPlacementTool)

            // Ruler tool for measurements
            this.rulerManager = new RulerManager(this.scene.getScene(), this.container, canvas)
            const rulerTool = new RulerTool(this.toolManager, this.scene.getScene(), this.rulerManager, this.dimensionOverlay)
            this.toolManager.registerTool(rulerTool)
            this.rulerManager.startLabelLoop(() => this.scene?.getCamera() ?? null)

            this.toolManager.setActiveTool('select')
            this.toolManager.enable()
          }

          // Wire gardenObjectState changes to incremental ground shape updates
          gardenObjectState.onChange((change) => {
            this.handleObjectStateChange(change)
          })

          // Expose Zone API globally
          exposeZoneAPI()

          // Wire up EditorAPI so MCP bridge can access viewer methods
          editorAPI.setViewer(this)

          // Connect to MCP server via WebSocket (only when explicitly enabled via ?mcp=true)
          if (isMcpEnabled()) {
            initWebSocketBridge()
          }

          debug.success('Viewer', 'Zone system initialized')
        }

        this.isInitialized = true
        debug.success('Viewer', 'All renderers initialized')
        this.setStatus(t('status.ready'))
      } catch (error) {
        debug.error('Scene', 'Failed to create scene', error)
        throw error
      }
    })()

    return this.initPromise
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInit(): Promise<void> {
    if (this.isInitialized) return
    if (this.initPromise) {
      await this.initPromise
    } else {
      throw new Error('Viewer not initialized. Call initialize() first.')
    }
  }

  /**
   * Create an empty local garden (not yet on server).
   * Used for ?new=true flow — garden gets POST'd on first save.
   */
  async createEmptyGarden(name: string): Promise<void> {
    await this.waitForInit()

    const garden: Garden = {
      id: '',          // empty = not yet created on server
      token: '',
      name,
      description: '',
      rotation: 0,
      isEditable: true,
      plants: [],
      buildings: [],
      groundShapes: [],
      properties: {},
    }

    this.currentGarden = garden

    // Import empty arrays so gardenObjectState is ready for editing
    if (isEditMode()) {
      gardenObjectState.importFromGarden([], [], [])
      saveService.suppressZoneDirty()
      saveService.markZonesClean()
      // Mark as new garden so save dot shows and first save creates on server
      saveService.markNewGarden()
    }

    // Set default camera
    this.scene?.focusOn(500, 500, 0, 1000, 1000)

    debug.success('Viewer', `Empty garden created: "${name}"`)

    this.onGardenLoaded?.(garden, {
      gardenId: '',
      loadTimeMs: 0,
      objectCount: 0,
    })
  }

  /**
   * Load and render a garden by ID
   */
  async loadGarden(gardenId: string): Promise<void> {
    debug.log('Garden', `Loading garden: ${gardenId}`)
    const loadStartTime = performance.now()

    // Wait for initialization
    await this.waitForInit()
    debug.log('Garden', 'Viewer initialized, proceeding with load')

    if (!this.scene || !this.groundRenderer || !this.plantRenderer || !this.buildingRenderer) {
      const error = new Error('Scene or renderers not initialized')
      debug.error('Garden', 'Missing components', {
        scene: !!this.scene,
        groundRenderer: !!this.groundRenderer,
        plantRenderer: !!this.plantRenderer,
        buildingRenderer: !!this.buildingRenderer
      })
      this.onError?.(error)
      return
    }

    try {
      // Fetch garden metadata
      this.setStatus(t('status.loadingGarden'))
      debug.log('API', `Fetching garden ${gardenId}...`)

      const authHeader = auth.getState()
      debug.log('API', 'Auth state', { isAuthenticated: authHeader.isAuthenticated })

      const apiClient = this.getApiClient()
      const garden = await apiClient.getGarden(gardenId)

      // Fetch category layer map if not already loaded
      if (this.categoryLayerMap.size === 0) {
        this.setStatus(t('status.loadingCategories'))
        this.categoryLayerMap = await apiClient.getCategoryLayerMap()
        debug.log('Garden', `Category layer map loaded: ${this.categoryLayerMap.size} categories`)
      }

      // Fetch all garden objects with products and textures in one API call
      this.setStatus(t('status.loadingAssets'))
      const assets = await apiClient.getGardenWithAssets(gardenId, {
        textureKinds: ['glb', 'top'],
        categoryLayerMap: this.categoryLayerMap
      })

      // Populate garden with objects
      garden.plants = assets.plants
      garden.buildings = assets.buildings
      garden.groundShapes = assets.groundShapes

      // Store asset maps
      this.products = assets.products
      this.modelUrls = assets.modelUrls
      this.textureUrls = assets.textureUrls

      this.currentGarden = garden

      // Import objects into GardenObjectState for editing
      if (isEditMode()) {
        gardenObjectState.importFromGarden(garden.plants, garden.buildings, garden.groundShapes)

        // Suppress dirty tracking during restore (zone + geodata imports fire onChange)
        saveService.suppressZoneDirty()

        // Restore zones/pins from garden properties (if saved previously)
        // Note: Rails JSON:API serializer converts JSONB keys to kebab-case,
        // so we normalize them back to camelCase before importing.
        const savedZones = garden.properties?.zones
        if (savedZones && typeof savedZones === 'object') {
          try {
            const parsed = savedZones as { zones?: unknown[]; pins?: unknown[] }
            const zones = Array.isArray(parsed.zones) ? parsed.zones.map(kebabToCamel) : []
            const pins = Array.isArray(parsed.pins) ? parsed.pins.map(kebabToCamel) : []
            if (zones.length > 0 || pins.length > 0) {
              zoneState.importState({ zones: zones as any[], pins: pins as any[] })
              debug.log('Garden', `Restored ${zones.length} zones, ${pins.length} pins from garden properties`)
            }
          } catch (error) {
            debug.warn('Garden', 'Failed to restore zones from garden properties', error)
          }
        }

        // Restore geo-anchor from garden properties
        // Note: Rails JSONB serializer converts camelCase keys to kebab-case,
        // so 'geoAnchor' is stored as 'geo-anchor' in the API response.
        const props = garden.properties ?? {}
        const savedAnchor = props['geoAnchor'] ?? props['geo-anchor']
        if (savedAnchor && typeof savedAnchor === 'object') {
          try {
            const anchor = kebabToCamel(savedAnchor) as GeoAnchor
            setGeoAnchor(anchor)
            this.schemeRenderer?.renderGeoAnchor(anchor)
            debug.log('Garden', 'Restored geo-anchor from garden properties')
          } catch (error) {
            debug.warn('Garden', 'Failed to restore geo-anchor from garden properties', error)
          }
        }

        // Restore parcel metadata from garden properties
        const savedParcel = garden.properties?.parcel
        if (savedParcel && typeof savedParcel === 'object') {
          try {
            setParcel(kebabToCamel(savedParcel) as ParcelMetadata)
            debug.log('Garden', 'Restored parcel metadata from garden properties')
          } catch (error) {
            debug.warn('Garden', 'Failed to restore parcel metadata from garden properties', error)
          }
        }

        // Restore locked object state from garden properties
        const savedLocked = props['lockedObjects'] ?? props['locked-objects']
        if (Array.isArray(savedLocked) && savedLocked.length > 0) {
          try {
            const serverIds = new Set(savedLocked as string[])
            for (const obj of gardenObjectState.getObjects()) {
              const sid = gardenObjectState.getServerId(obj.id)
              if (sid && serverIds.has(sid)) {
                gardenObjectState.updateObject(obj.id, { locked: true })
              }
            }
            debug.log('Garden', `Restored lock state for ${savedLocked.length} objects`)
          } catch (error) {
            debug.warn('Garden', 'Failed to restore locked objects', error)
          }
        }

        // Clear any dirty state caused by restoring locks/properties above
        gardenObjectState.markAllClean()

        // Re-enable dirty tracking after all restores
        saveService.markZonesClean()
      }

      debug.success('API', `Garden loaded: "${garden.name}"`, {
        plants: garden.plants.length,
        buildings: garden.buildings.length,
        groundShapes: garden.groundShapes.length
      })

      // Collect all GLB URLs for parallel preloading
      const glbUrls: string[] = []
      for (const files of this.modelUrls.values()) {
        const glbFile = files.find(f => f.kind === 'glb')
        if (glbFile?.fileUrl) {
          glbUrls.push(glbFile.fileUrl)
        }
      }

      // Preload all models in parallel (if any)
      if (glbUrls.length > 0) {
        this.setStatus(t('status.preloadingModels'))
        debug.log('Preload', `Preloading ${glbUrls.length} GLB models in parallel...`)
        await modelLoader.preloadMany(glbUrls)
        debug.success('Preload', `All ${glbUrls.length} models preloaded`)
      }

      // Prefetch billboard textures for plants without GLB models
      this.billboardUrls.clear()
      await this.prefetchBillboardTextures(garden.plants)

      // Preload all textures in parallel (billboard, top-view, ground)
      const textureUrls: string[] = []

      // Collect billboard texture URLs
      for (const file of this.billboardUrls.values()) {
        if (file.fileUrl) {
          textureUrls.push(file.fileUrl)
        }
      }

      // Collect top-view texture URLs
      for (const files of this.textureUrls.values()) {
        for (const file of files) {
          if (file.kind === 'top' && file.fileUrl) {
            textureUrls.push(file.fileUrl)
          }
        }
      }

      // Preload textures in parallel (if any)
      if (textureUrls.length > 0) {
        this.setStatus(t('status.preloadingTextures'))
        await texturePreloader.preloadMany(textureUrls)
      }

      // Focus camera on garden center BEFORE rendering
      // This prevents camera "snap" when user can see scene through semi-transparent overlay
      this.focusOnGarden(garden)

      // Set sun direction based on garden rotation
      this.scene!.setSunDirection(garden.rotation)

      // Render all objects
      await this.renderGarden(garden)

      // Sync plant renderer with current view mode (for &camera=3D startup)
      this.plantRenderer?.setViewMode(this.scene!.getViewMode())

      this.setStatus(t('status.gardenLoaded'))
      debug.success('Garden', 'Garden fully rendered')

      this.onGardenLoaded?.(garden, {
        gardenId,
        loadTimeMs: Math.round(performance.now() - loadStartTime),
        objectCount: garden.plants.length + garden.buildings.length + garden.groundShapes.length
      })

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      debug.error('Garden', 'Failed to load garden', err)
      this.setStatus(`${t('error.prefix')} ${err.message}`)

      this.onError?.(err)
    }
  }

  /**
   * Prefetch billboard textures for plants without GLB models
   * This moves data fetching to the orchestration layer (away from PlantRenderer)
   */
  private async prefetchBillboardTextures(plants: Plant[]): Promise<void> {
    // Collect unique product IDs that need billboard fallback (no GLB model)
    const productIdsNeedingBillboard = new Set<string>()
    for (const plant of plants) {
      if (plant.markedForDestruction) continue
      const productId = plant.productId
      if (!productId) continue

      // Check if product has GLB model
      const files = this.modelUrls.get(productId) || []
      const hasGlb = files.some(f => f.kind === 'glb')
      if (!hasGlb) {
        productIdsNeedingBillboard.add(productId)
      }
    }

    if (productIdsNeedingBillboard.size === 0) {
      debug.log('Billboard', 'No billboard textures needed (all plants have GLB models)')
      return
    }

    debug.log('Billboard', `Prefetching ${productIdsNeedingBillboard.size} billboard textures...`)

    // Fetch billboard textures in parallel
    const apiClient = this.getApiClient()
    const fetchPromises = [...productIdsNeedingBillboard].map(async productId => {
      try {
        const billboardFile = await apiClient.getBillboardTexture(productId)
        if (billboardFile) {
          this.billboardUrls.set(productId, billboardFile)
        }
      } catch (error) {
        debug.warn('Billboard', `Failed to fetch billboard for product ${productId}`, error)
      }
    })

    await Promise.all(fetchPromises)
    debug.success('Billboard', `Prefetched ${this.billboardUrls.size} billboard textures`)
  }

  /**
   * Render all garden objects
   */
  private async renderGarden(garden: Garden): Promise<void> {
    debug.group('Rendering garden')

    // Clear existing objects
    debug.log('Render', 'Clearing existing objects...')
    this.scene!.clearAllObjects()
    this.schemeRenderer!.dispose()

    // Render scheme image first (background layer)
    if (garden.schemeImage?.imageUrl) {
      this.setStatus(t('status.renderingScheme'))
      debug.log('Render', 'Rendering scheme background image...')
      await this.schemeRenderer!.load(garden.schemeImage)
    }

    // Render scheme outline if present
    if (garden.schemeOutline) {
      debug.log('Render', 'Rendering scheme outline...')
      this.schemeRenderer!.renderOutline(garden.schemeOutline)
      // Add outline to selection manager for boundary editing
      this.refreshBoundaryInSelectionManager()
    }

    // Render ground shapes (bottom layer)
    // In edit mode, use gardenObjectState data (already in center convention)
    // In view mode, convert API data (top-left) to center convention for GroundRenderer
    const groundShapesToRender = isEditMode()
      ? gardenObjectState.getGroundShapes()
      : garden.groundShapes.map(s => ({
          ...s,
          x: s.x + s.width / 2,
          y: s.y + s.height / 2,
          points: s.points?.map(p => ({ x: p.x - s.width / 2, y: p.y - s.height / 2 })),
        } as GroundShape))
    this.setStatus(t('status.renderingGround'))
    debug.log('Render', `Rendering ${groundShapesToRender.length} ground shapes...`)
    await this.groundRenderer!.renderShapes(groundShapesToRender, this.textureUrls)
    debug.success('Render', `Ground: ${this.groundRenderer!.count} shapes rendered`)

    // Register ground shapes for elevation calculation (buildings and plants query this)
    elevationService.setGroundShapes(groundShapesToRender, this.products)

    // Render buildings
    // In edit mode, use gardenObjectState data (already in center convention)
    // In view mode, convert API data (top-left) to center convention
    const buildingsToRender = isEditMode()
      ? gardenObjectState.getBuildings()
      : garden.buildings.map(b => ({
          ...b,
          x: b.x + b.width / 2,
          y: b.y + b.height / 2,
        } as Building))
    this.setStatus(t('status.renderingBuildings'))
    debug.log('Render', `Rendering ${buildingsToRender.length} buildings...`)
    await this.buildingRenderer!.renderBuildings(buildingsToRender, this.modelUrls, this.products)
    debug.success('Render', `Buildings: ${this.buildingRenderer!.count} rendered`)

    // Register buildings for elevation calculation (plants query this)
    elevationService.setBuildings(buildingsToRender, this.products)

    // Render plants (top layer)
    // In edit mode, use gardenObjectState data (already in center convention)
    // In view mode, convert API data (top-left) to center convention
    const plantsToRender = isEditMode()
      ? gardenObjectState.getPlants()
      : garden.plants.map(p => ({
          ...p,
          x: p.x + p.width / 2,
          y: p.y + p.height / 2,
        } as Plant))
    this.setStatus(t('status.renderingPlants'))
    debug.log('Render', `Rendering ${plantsToRender.length} plants...`)
    await this.plantRenderer!.renderPlants(plantsToRender, this.modelUrls, this.textureUrls, this.billboardUrls, this.products)
    debug.success('Render', `Plants: ${this.plantRenderer!.count} rendered`)

    debug.groupEnd()
  }

  /**
   * Focus camera on the center of the garden
   */
  private focusOnGarden(garden: Garden): void {
    if (!this.scene) return

    // Calculate bounding box of all objects
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity

    // In edit mode, use gardenObjectState (center convention)
    // In view mode, use raw API data (top-left convention)
    const allObjects = isEditMode()
      ? gardenObjectState.getObjects()
      : [...garden.plants, ...garden.buildings, ...garden.groundShapes]

    if (allObjects.length === 0) {
      debug.log('Camera', 'No objects, using default view')
      this.scene.focusOn(500, 500, 0, 1000, 1000)
      return
    }

    for (const obj of allObjects) {
      if (isEditMode()) {
        // Center convention: x,y = center
        minX = Math.min(minX, obj.x - obj.width / 2)
        maxX = Math.max(maxX, obj.x + obj.width / 2)
        minY = Math.min(minY, obj.y - obj.height / 2)
        maxY = Math.max(maxY, obj.y + obj.height / 2)
      } else {
        // API convention: x,y = top-left
        minX = Math.min(minX, obj.x)
        maxX = Math.max(maxX, obj.x + obj.width)
        minY = Math.min(minY, obj.y)
        maxY = Math.max(maxY, obj.y + obj.height)
      }
    }

    // Center and size of all objects
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2
    const boundsWidth = maxX - minX
    const boundsHeight = maxY - minY

    debug.log('Camera', `Focusing on center: (${centerX}, ${centerY}), bounds: ${boundsWidth}x${boundsHeight}`)
    this.scene.focusOn(centerX, centerY, 0, boundsWidth, boundsHeight)
  }

  /**
   * Get current garden data
   */
  getGarden(): Garden | null {
    return this.currentGarden
  }

  /**
   * Get object counts for UI display
   */
  getStats(): { plants: number; buildings: number; shapes: number; hasScheme: boolean } {
    return {
      plants: this.plantRenderer?.count || 0,
      buildings: this.buildingRenderer?.count || 0,
      shapes: this.groundRenderer?.count || 0,
      hasScheme: this.schemeRenderer?.isLoaded() || false
    }
  }

  /**
   * Set scheme layer visibility
   */
  setSchemeVisible(visible: boolean): void {
    this.schemeRenderer?.setVisible(visible)
  }

  /**
   * Get scheme layer visibility
   */
  isSchemeVisible(): boolean {
    return this.schemeRenderer?.isVisible() ?? false
  }

  /**
   * Set or update the property boundary outline
   * Used when importing boundary from geoportal
   */
  setSchemeOutline(outline: { x: number; y: number; points: Array<{ x: number; y: number }> } | null): void {
    // Store for save access
    if (this.currentGarden) {
      this.currentGarden.schemeOutline = outline ?? undefined
    }

    if (!this.schemeRenderer) {
      debug.warn('Viewer', 'SchemeRenderer not initialized')
      return
    }

    if (outline) {
      debug.log('Viewer', `Setting scheme outline with ${outline.points.length} points`)
      this.schemeRenderer.renderOutline(outline)
    } else {
      debug.log('Viewer', 'Clearing scheme outline')
      this.schemeRenderer.renderOutline({ x: 0, y: 0, points: [] })
    }

    // Refresh SelectionManager reference (outline mesh is destroyed/recreated)
    this.refreshBoundaryInSelectionManager()
  }

  /**
   * Clear the property boundary outline
   */
  clearSchemeOutline(): void {
    if (this.currentGarden) {
      this.currentGarden.schemeOutline = undefined
    }
    if (!this.schemeRenderer) return
    this.schemeRenderer.renderOutline({ x: 0, y: 0, points: [] })
    this.refreshBoundaryInSelectionManager()
  }

  /**
   * Get current scheme outline data (for saving)
   */
  getSchemeOutline(): SchemeOutline | undefined {
    return this.currentGarden?.schemeOutline
  }

  /**
   * Render the geo-anchor pin marker on the map layer.
   * Call after setGeoAnchor() to show the marker, or pass null to remove.
   */
  renderGeoAnchor(anchor: GeoAnchor | null): void {
    this.schemeRenderer?.renderGeoAnchor(anchor)
  }

  /**
   * Set garden rotation (north direction) and update sun/compass.
   * Used by BoundaryAdapter when rotating the boundary.
   */
  setGardenRotation(degrees: number): void {
    if (this.currentGarden) {
      this.currentGarden.rotation = ((degrees % 360) + 360) % 360
      this.scene?.setSunDirection(this.currentGarden.rotation)
    }
  }

  /**
   * G12: Rotate boundary points around geo-anchor origin (0,0) and update garden.rotation.
   * The geo-anchor is always at editor origin (0,0), so that's our pivot.
   */
  rotateBoundary(angleDeg: number): void {
    const outline = this.currentGarden?.schemeOutline
    if (!outline || outline.points.length < 3) return

    // Pivot = geo-anchor editor origin (always 0,0)
    const pivot = { x: 0, y: 0 }

    // Rotate all points (they are outline-relative, so absolute = outline.x/y + point)
    const absolutePoints = outline.points.map(p => ({
      x: outline.x + p.x,
      y: outline.y + p.y,
    }))
    const rotatedAbsolute = rotateBoundaryPoints(absolutePoints, angleDeg, pivot)

    // Also rotate the outline origin
    const rotatedOrigin = rotateBoundaryPoints([{ x: outline.x, y: outline.y }], angleDeg, pivot)[0]

    // Convert back to outline-relative
    const newOutline: SchemeOutline = {
      x: rotatedOrigin.x,
      y: rotatedOrigin.y,
      points: rotatedAbsolute.map(p => ({
        x: p.x - rotatedOrigin.x,
        y: p.y - rotatedOrigin.y,
      })),
    }

    // Update boundary
    this.setSchemeOutline(newOutline)

    // Update garden rotation and sun direction
    if (this.currentGarden) {
      this.currentGarden.rotation = ((this.currentGarden.rotation + angleDeg) % 360 + 360) % 360
      this.scene?.setSunDirection(this.currentGarden.rotation)
    }

    debug.log('Viewer', `Boundary rotated by ${angleDeg.toFixed(1)}° (garden rotation: ${this.currentGarden?.rotation.toFixed(1)}°)`)
  }

  /**
   * G13: Load a WMS map background and render it immediately
   */
  async loadWmsBackground(schemeImage: import('@/types').SchemeImage): Promise<void> {
    if (!this.schemeRenderer) return
    if (this.currentGarden) {
      this.currentGarden.schemeImage = schemeImage
    }
    await this.schemeRenderer.load(schemeImage)
  }

  /**
   * G13: Clear the map background image from the scene
   */
  clearMapBackground(): void {
    if (this.currentGarden) {
      this.currentGarden.schemeImage = { x: 0, y: 0, scale: 1 }
    }
    // Dispose removes the mesh from scene
    this.schemeRenderer?.dispose()
    // Re-render outline + anchor if they exist (dispose clears everything)
    if (this.currentGarden?.schemeOutline) {
      this.schemeRenderer?.renderOutline(this.currentGarden.schemeOutline)
      this.refreshBoundaryInSelectionManager()
    }
    const anchor = getGeoAnchor()
    if (anchor) {
      this.schemeRenderer?.renderGeoAnchor(anchor)
    }
  }

  /**
   * Check if a map background image is loaded
   */
  hasMapBackground(): boolean {
    return this.schemeRenderer?.isLoaded() ?? false
  }

  /**
   * Get the SchemeRenderer (for advanced access)
   */
  getSchemeRenderer(): SchemeRenderer | null {
    return this.schemeRenderer
  }

  /**
   * Set view mode (2D or 3D)
   */
  setViewMode(mode: ViewMode): void {
    this.scene?.setViewMode(mode)
    this.plantRenderer?.setViewMode(mode)
  }

  /**
   * Get current view mode
   */
  getViewMode(): ViewMode {
    return this.scene?.getViewMode() ?? '2d'
  }

  /**
   * Enable or disable walk mode (first-person view)
   */
  setWalkMode(enabled: boolean): void {
    this.scene?.setWalkMode(enabled)
  }

  /**
   * Check if walk mode is active
   */
  isWalkMode(): boolean {
    return this.scene?.isWalkMode() ?? false
  }

  /**
   * Set callback for walk mode changes
   */
  setOnWalkModeChange(callback: (enabled: boolean) => void): void {
    this.scene?.setOnWalkModeChange(callback)
  }

  /**
   * Set callback for view mode changes
   */
  setOnViewModeChange(callback: (mode: ViewMode) => void): void {
    this.scene?.setOnViewModeChange(callback)
  }

  /**
   * Set visibility of a specific layer
   */
  setLayerVisible(layer: LayerName, visible: boolean): void {
    this.scene?.setLayerVisible(layer, visible)
    // Metadata layer also controls SchemeRenderer and zones/pins
    if (layer === 'metadata') {
      this.schemeRenderer?.setVisible(visible)
      this.zoneRenderer?.setVisible(visible)
      this.pinRenderer?.setVisible(visible)
    }
  }

  /**
   * Toggle visibility of a specific layer
   * @returns The new visibility state
   */
  toggleLayer(layer: LayerName): boolean {
    const newState = this.scene?.toggleLayer(layer) ?? true
    // Metadata layer also controls zones/pins
    if (layer === 'metadata') {
      this.zoneRenderer?.setVisible(newState)
      this.pinRenderer?.setVisible(newState)
    }
    // Map layer controls SchemeRenderer (background image)
    if (layer === 'map') {
      this.schemeRenderer?.setVisible(newState)
    }
    return newState
  }

  /**
   * Get current layer visibility state
   */
  getLayerVisibility(): LayerVisibility {
    return this.scene?.getLayerVisibility() ?? {
      plants: true,
      buildings: true,
      ground: true,
      metadata: true,
      map: true
    }
  }

  /**
   * Check if a specific layer is visible
   */
  isLayerVisible(layer: LayerName): boolean {
    return this.scene?.isLayerVisible(layer) ?? true
  }

  /**
   * Set callback for layer visibility changes
   */
  setOnLayerVisibilityChange(callback: (layer: LayerName, visible: boolean) => void): void {
    this.scene?.setOnLayerVisibilityChange(callback)
  }

  // ============================================================================
  // Canvas Controls API (compass + zoom)
  // ============================================================================

  getAzimuthalAngle(): number {
    return this.scene?.getAzimuthalAngle() ?? 0
  }

  getGardenRotation(): number {
    return this.scene?.getGardenRotation() ?? 0
  }

  zoomIn(): void {
    this.scene?.zoomIn()
  }

  zoomOut(): void {
    this.scene?.zoomOut()
  }

  getZoomLimits(): { atMin: boolean; atMax: boolean } {
    return this.scene?.getZoomLimits() ?? { atMin: false, atMax: false }
  }

  resetNorth(): void {
    this.scene?.resetNorth()
  }

  // ============================================================================
  // Selection API (only available in edit mode)
  // ============================================================================

  /**
   * Check if edit mode is enabled
   */
  isEditMode(): boolean {
    return isEditMode()
  }

  /**
   * Get the GardenScene instance (for advanced access)
   */
  getScene(): GardenScene | null {
    return this.scene
  }

  /**
   * Set callback for object selection changes
   */
  setOnObjectSelected(callback: (object: SelectableObject | null) => void): void {
    // Wrap callback to extract object from SelectionResult
    this.scene?.getSelectionManager()?.setOnSelectionChange((result) => {
      callback(result?.object ?? null)
    })
  }

  /**
   * Set callback for object hover changes
   */
  setOnObjectHovered(callback: (object: SelectableObject | null) => void): void {
    // Wrap callback to extract object from SelectionResult
    this.scene?.getSelectionManager()?.setOnHoverChange((result) => {
      callback(result?.object ?? null)
    })
  }

  /**
   * Get currently selected object
   */
  getSelectedObject(): SelectableObject | null {
    return this.scene?.getSelectionManager()?.getSelected() ?? null
  }

  /**
   * Get currently hovered object
   */
  getHoveredObject(): SelectableObject | null {
    return this.scene?.getSelectionManager()?.getHovered() ?? null
  }

  /**
   * Programmatically select an object
   */
  selectObject(object: SelectableObject): void {
    this.scene?.getSelectionManager()?.select(object)
  }

  /**
   * Request auto-selection of an object after its mesh finishes rendering.
   * Used by PlaceObjectTool to select the placed object in single mode.
   */
  selectAfterRender(objectId: string): void {
    this.pendingSelectId = objectId
  }

  /**
   * Try to select the pending object if its mesh now exists.
   * Called after addPlant/addBuilding resolves so the mesh is in the scene.
   */
  private tryPendingSelect(objectId: string): void {
    if (this.pendingSelectId !== objectId) return
    this.pendingSelectId = null
    const obj = gardenObjectState.getObjectById(objectId)
    if (!obj) return
    this.selectObject(obj as SelectableObject)
  }

  /**
   * Set the active layer for selection filtering.
   * Only objects on this layer can be selected/hovered.
   * null = no filtering (all layers selectable).
   */
  setActiveLayer(layer: string | null): void {
    this.scene?.getSelectionManager()?.setActiveLayer(layer)

    if (this.schemeRenderer) {
      const isMap = layer === 'map'
      // Make boundary outline thicker when map tab is active (easier to see and click)
      const rerendered = this.schemeRenderer.setOutlineThick(isMap)
      if (rerendered) {
        this.refreshBoundaryInSelectionManager()
      }
      // Geo-anchor marker only visible on map layer
      this.schemeRenderer.setAnchorMarkerVisible(isMap)
    }
  }

  /**
   * Deselect all objects
   */
  deselectAll(): void {
    this.scene?.getSelectionManager()?.deselect()
  }

  // ============================================================================
  // Preview Capture
  // ============================================================================

  /**
   * Capture a top-down preview image of the garden (JPEG data URI, max 615px).
   * Returns null if scene not initialized or no objects to capture.
   */
  capturePreview(): string | null {
    if (!this.scene) return null

    const objects = gardenObjectState.getObjects()
    const zones = zoneState.getZones()
    if (objects.length === 0 && zones.length === 0) return null

    // Compute bounding box of all objects AND zones (center convention)
    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity
    for (const obj of objects) {
      minX = Math.min(minX, obj.x - obj.width / 2)
      maxX = Math.max(maxX, obj.x + obj.width / 2)
      minY = Math.min(minY, obj.y - obj.height / 2)
      maxY = Math.max(maxY, obj.y + obj.height / 2)
    }
    for (const zone of zones) {
      minX = Math.min(minX, zone.x - zone.width / 2)
      maxX = Math.max(maxX, zone.x + zone.width / 2)
      minY = Math.min(minY, zone.y - zone.height / 2)
      maxY = Math.max(maxY, zone.y + zone.height / 2)
    }

    return this.scene.capturePreview(615, { minX, maxX, minY, maxY })
  }

  // ============================================================================
  // Zone API (only available in edit mode)
  // ============================================================================

  /**
   * Get tool manager (edit mode only)
   */
  getToolManager(): ToolManager | null {
    return this.toolManager
  }

  /**
   * Get ruler manager (edit mode only)
   */
  getRulerManager(): RulerManager | null {
    return this.rulerManager
  }

  /**
   * Get zone renderer
   */
  getZoneRenderer(): ZoneRenderer | null {
    return this.zoneRenderer
  }

  /**
   * Get pin renderer
   */
  getPinRenderer(): PinRenderer | null {
    return this.pinRenderer
  }

  /**
   * Handle incremental ground shape changes from gardenObjectState.
   * Uses targeted add/update/remove to avoid flickering existing shapes.
   */
  private handleObjectStateChange(change: StateChange): void {
    if (!change.objectId && change.type !== 'bulk') return

    const obj = change.objectId ? gardenObjectState.getObjectById(change.objectId) : undefined
    const objType = obj?.type

    // Determine which layer the object belongs to
    const isGround = objType && ['rectangle', 'polygon', 'ellipse', 'path'].includes(objType)
    const isBuilding = objType === 'building'
    const isPlant = objType === 'plant'

    // --- Ground shapes ---
    if (this.groundRenderer && (isGround || change.type === 'bulk' || change.type === 'delete')) {
      switch (change.type) {
        case 'add':
        case 'restore':
          if (isGround && obj) {
            this.groundRenderer.addShape(obj as GroundShape, this.textureUrls)
          }
          break
        case 'update':
          if (isGround && obj) {
            this.groundRenderer.updateShape(obj as GroundShape, this.textureUrls)
            // Re-sort layers when zIndex changes (bring to front / send to back)
            if (change.updatedKeys?.includes('zIndex')) {
              this.groundRenderer.resortLayers()
            }
          }
          break
        case 'delete':
          if (change.objectId) {
            this.groundRenderer.removeShape(change.objectId)
          }
          break
        case 'bulk':
          this.groundRenderer.clear()
          this.groundRenderer.renderShapes(
            gardenObjectState.getGroundShapes(),
            this.textureUrls
          )
          break
      }

      // Update elevation service for ground changes
      elevationService.setGroundShapes(gardenObjectState.getGroundShapes(), this.products)
      // Re-register buildings so effectiveElevation (ground + own) is recalculated
      elevationService.setBuildings(gardenObjectState.getBuildings(), this.products)

      // Refresh plant/building Y positions after elevation data changed
      if (this.buildingRenderer) this.buildingRenderer.refreshElevations()
      if (this.plantRenderer) this.plantRenderer.refreshElevations()
    }

    // --- Buildings ---
    if (this.buildingRenderer && (isBuilding || change.type === 'bulk' || change.type === 'delete')) {
      switch (change.type) {
        case 'add':
        case 'restore':
          if (isBuilding && obj) {
            const building = obj as Building
            // Show immediately (GLB if cached, fallback box otherwise)
            const buildingReady = this.buildingRenderer.addBuilding(building, this.modelUrls, this.products)
            // If no GLB was cached, fetch it and swap in the real model
            if (building.productId && !this.modelUrls.has(building.productId)) {
              this.ensureProductModel(building.productId).then(async () => {
                if (this.modelUrls.has(building.productId!)) {
                  this.buildingRenderer!.removeBuilding(building.id)
                  await this.buildingRenderer!.addBuilding(building, this.modelUrls, this.products)
                }
                this.tryPendingSelect(building.id)
              })
            } else {
              buildingReady.then(() => this.tryPendingSelect(building.id))
            }
          }
          break
        case 'update':
          if (isBuilding && obj) {
            this.buildingRenderer.updateBuilding(obj as Building)
          }
          break
        case 'delete':
          if (change.objectId) {
            this.buildingRenderer.removeBuilding(change.objectId)
          }
          break
        case 'bulk':
          this.buildingRenderer.clear()
          this.buildingRenderer.renderBuildings(
            gardenObjectState.getBuildings(), this.modelUrls, this.products
          )
          break
      }

      // Update elevation service for building changes
      elevationService.setBuildings(gardenObjectState.getBuildings(), this.products)
    }

    // --- Plants ---
    if (this.plantRenderer && (isPlant || change.type === 'bulk' || change.type === 'delete')) {
      switch (change.type) {
        case 'add':
        case 'restore':
          if (isPlant && obj) {
            const plant = obj as Plant
            // Show immediately (GLB if cached, billboard/fallback otherwise)
            const plantReady = this.plantRenderer.addPlant(plant, this.modelUrls, this.products, this.textureUrls, this.billboardUrls)
            // If no GLB was cached, try fetching GLB first, then billboard
            if (plant.productId && !this.modelUrls.has(plant.productId)) {
              this.ensureProductModel(plant.productId).then(async () => {
                if (this.modelUrls.has(plant.productId!)) {
                  // GLB found — swap in real model
                  this.plantRenderer!.removePlant(plant.id)
                  await this.plantRenderer!.addPlant(plant, this.modelUrls, this.products, this.textureUrls, this.billboardUrls)
                } else if (!this.billboardUrls.has(plant.productId!)) {
                  // No GLB — try fetching billboard texture
                  await this.ensureProductBillboard(plant.productId!)
                  if (this.billboardUrls.has(plant.productId!)) {
                    this.plantRenderer!.removePlant(plant.id)
                    await this.plantRenderer!.addPlant(plant, this.modelUrls, this.products, this.textureUrls, this.billboardUrls)
                  }
                }
                this.tryPendingSelect(plant.id)
              })
            } else {
              plantReady.then(() => this.tryPendingSelect(plant.id))
            }
          }
          break
        case 'update':
          if (isPlant && obj) {
            this.plantRenderer.updatePlant(obj as Plant)
          }
          break
        case 'delete':
          if (change.objectId) {
            this.plantRenderer.removePlant(change.objectId)
          }
          break
        case 'bulk':
          this.plantRenderer.clear()
          this.plantRenderer.renderPlants(
            gardenObjectState.getPlants(), this.modelUrls, this.textureUrls, this.billboardUrls, this.products
          )
          break
      }
    }
  }

  /**
   * Ensure a product is in the products cache.
   * Called when placing new objects from the catalog.
   */
  ensureProductCached(product: Product): void {
    if (!this.products.has(product.id)) {
      this.products.set(product.id, product)
      debug.log('Viewer', `Cached product ${product.id} (${product.name})`)
    }
  }

  /** Object ID to auto-select after its mesh is rendered (single placement mode) */
  private pendingSelectId: string | null = null

  /** In-flight preload promises to avoid duplicate API calls */
  private preloadPromises: Map<string, Promise<void>> = new Map()

  /**
   * Preload a product's assets (fetch URLs + download GLB/textures).
   * If no GLB exists, downloads billboard + top-view textures instead.
   * Called when user selects a product so placement is instantaneous.
   */
  async preloadProductModel(productId: string): Promise<void> {
    // Deduplicate: return existing in-flight preload if any
    const existing = this.preloadPromises.get(productId)
    if (existing) return existing

    const promise = this.doPreloadProduct(productId).finally(() => {
      this.preloadPromises.delete(productId)
    })
    this.preloadPromises.set(productId, promise)
    return promise
  }

  private async doPreloadProduct(productId: string): Promise<void> {
    await this.ensureProductModel(productId)

    const glbFiles = this.modelUrls.get(productId)
    const modelUrl = glbFiles?.find(f => f.kind === 'glb')?.fileUrl

    if (modelUrl) {
      // Preload actual GLB file into model loader cache
      const { modelLoader } = await import('@/scene/ModelLoader')
      modelLoader.load(modelUrl).catch(() => {
        debug.warn('Viewer', `Failed to preload GLB for product ${productId}`)
      })
    } else {
      // No GLB — preload billboard + top-view texture images
      await this.ensureProductBillboard(productId)
    }

    // Preload actual texture image files (billboard + top-view PNGs)
    const { texturePreloader } = await import('@/scene/TexturePreloader')
    const urls: string[] = []

    const topFiles = this.textureUrls.get(productId)
    const topUrl = topFiles?.find(f => f.kind === 'top')?.fileUrl
    if (topUrl) urls.push(topUrl)

    const billboardFile = this.billboardUrls.get(productId)
    if (billboardFile?.fileUrl) urls.push(billboardFile.fileUrl)

    if (urls.length > 0) {
      texturePreloader.preloadMany(urls)
    }
  }

  /**
   * Ensure a product's GLB model is in the modelUrls cache.
   * Fetches from API if not already cached.
   */
  private async ensureProductModel(productId: string): Promise<void> {
    const needGlb = !this.modelUrls.has(productId)
    const needTop = !this.textureUrls.has(productId)
    if (!needGlb && !needTop) return

    const kinds: string[] = []
    if (needGlb) kinds.push('glb')
    if (needTop) kinds.push('top')

    debug.log('Viewer', `Fetching textures [${kinds.join(',')}] for product ${productId}...`)
    try {
      const textures = await this.getApiClient().getTextures([productId], { kinds })
      const files = textures.get(productId)
      if (files && files.length > 0) {
        const glbFiles = files.filter(f => f.kind === 'glb')
        const topFiles = files.filter(f => f.kind === 'top')
        if (glbFiles.length > 0) {
          this.modelUrls.set(productId, glbFiles)
          debug.success('Viewer', `Cached GLB model for product ${productId}`)
        }
        if (topFiles.length > 0) {
          this.textureUrls.set(productId, topFiles)
          debug.success('Viewer', `Cached top texture for product ${productId}`)
        }
        if (glbFiles.length === 0 && topFiles.length === 0) {
          debug.log('Viewer', `No GLB/top textures found for product ${productId}`)
        }
      } else {
        debug.log('Viewer', `No textures found for product ${productId}`)
      }
    } catch (error) {
      debug.warn('Viewer', `Failed to fetch textures for product ${productId}`, error)
    }
  }

  /**
   * Ensure a product's billboard texture is in the billboardUrls cache.
   * Called when placing plants that may not have GLB models.
   */
  private async ensureProductBillboard(productId: string): Promise<void> {
    if (this.billboardUrls.has(productId)) return

    try {
      const billboardFile = await this.getApiClient().getBillboardTexture(productId)
      if (billboardFile) {
        this.billboardUrls.set(productId, billboardFile)
        debug.success('Viewer', `Cached billboard texture for product ${productId}`)
      }
    } catch (error) {
      debug.warn('Viewer', `Failed to fetch billboard for product ${productId}`, error)
    }
  }

  /**
   * Ensure a product's textures are in the textureUrls cache.
   * Called before creating ground shapes so GroundRenderer can find the texture.
   */
  private ensureProductTextures(product: Product): void {
    if (this.textureUrls.has(product.id)) return
    if (!product.topTextureUrl) return

    const textureFile: ProductFile = {
      id: `top_${product.id}`,
      productId: product.id,
      kind: 'top',
      fileUrl: product.topTextureUrl,
      width: product.topTextureWidth,
      height: product.topTextureHeight,
    }
    this.textureUrls.set(product.id, [textureFile])
    debug.log('Viewer', `Cached top texture for product ${product.id}`)
  }

  /**
   * Get the category layer map (for tools that need it)
   */
  getCategoryLayerMap(): Map<string, Layer> {
    return this.categoryLayerMap
  }

  /**
   * Get the product code for a given product ID.
   * Returns undefined if the product is not cached.
   */
  getProductCode(productId: string): string | undefined {
    return this.products.get(productId)?.code
  }

  /** Previous outline group reference (for cleanup from SelectionManager) */
  private previousOutlineGroup: THREE.Group | null = null

  /**
   * Refresh the boundary outline group in SelectionManager.
   * Called after renderOutline() which destroys + recreates the mesh.
   */
  private refreshBoundaryInSelectionManager(): void {
    if (!this.schemeRenderer || !this.scene) return
    const sm = this.scene.getSelectionManager()
    if (!sm) return

    // Remove previous stale group
    if (this.previousOutlineGroup) {
      sm.removeSelectableGroup(this.previousOutlineGroup)
      this.previousOutlineGroup = null
    }

    const outlineGroup = this.schemeRenderer.getOutlineGroup()
    if (outlineGroup) {
      sm.addSelectableGroup(outlineGroup)
      this.previousOutlineGroup = outlineGroup

      // If boundary is currently selected, refresh the stale mesh reference
      const selected = sm.getSelected()
      if (selected && selected.id === 'boundary') {
        sm.updateSelectedMesh('boundary', outlineGroup)
      }
    }
  }

  /**
   * Unload current garden (clears scene but keeps viewer ready for next garden)
   */
  unloadGarden(): void {
    debug.log('Viewer', 'Unloading garden...')

    // Clear all rendered objects
    this.plantRenderer?.clear()
    this.buildingRenderer?.clear()
    this.groundRenderer?.clear()
    this.schemeRenderer?.dispose()

    // Clear zone state and object state (edit mode)
    if (isEditMode()) {
      zoneState.clearAll()
      gardenObjectState.clearAll()
      this.zoneRenderer?.dispose()
      this.pinRenderer?.dispose()
    }

    // Clear scene objects
    this.scene?.clearAllObjects()

    // Clear cached data
    this.currentGarden = null
    this.modelUrls.clear()
    this.textureUrls.clear()
    this.billboardUrls.clear()
    this.products.clear()

    // Clear elevation service
    elevationService.clear()

    debug.success('Viewer', 'Garden unloaded')
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    debug.log('Viewer', 'Disposing viewer...')
    this.plantRenderer?.clear()
    this.buildingRenderer?.clear()
    this.groundRenderer?.clear()
    this.schemeRenderer?.dispose()
    this.zoneRenderer?.dispose()
    this.pinRenderer?.dispose()
    this.toolManager?.dispose()
    this.rulerManager?.dispose()
    this.dimensionOverlay?.dispose()
    disposeWebSocketBridge()
    this.scene?.dispose()
    this.scene = null
    this.isInitialized = false
    this.initPromise = null
  }

  private setStatus(status: string): void {
    debug.log('Status', status)
    this.onStatusChange?.(status)
  }
}

/**
 * Convert kebab-case keys in a plain object to camelCase.
 * Rails JSON:API serializer converts JSONB keys (e.g. zoneType → zone-type).
 */
function kebabToCamel(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(kebabToCamel)
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const camelKey = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    out[camelKey] = kebabToCamel(value)
  }
  return out
}
